import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ItemDetailsService } from '../../services/item-details.service';

export type OldSalesPriceRow = {
    orderNumber?: string;
    lineNumber?: string;
    description?: string;
    quantity?: string;
    salesPrice?: string;
    orderDate?: string;
};

@Component({
    selector: 'app-old-sales-price-popup',
    standalone: true,
    imports: [CommonModule, FormsModule, DialogModule, ButtonModule, InputTextModule, AutoCompleteModule, TableModule],
    templateUrl: './old-sales-price-popup.component.html',
    styleUrls: ['./old-sales-price-popup.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class OldSalesPricePopupComponent implements OnChanges {
    @Input() visible = false;
    @Input() customer = '';
    @Input() division = '';
    @Input() warehouse = '';
    @Input() company = '';
    @Input() itemNo = '';

    @Output() visibleChange = new EventEmitter<boolean>();

    selectedSuggestion: any = null;
    suggestions: any[] = [];
    searchingItems = false;
    selectedItemNo = '';
    loading = false;
    searched = false;
    errorMessage = '';
    rows: OldSalesPriceRow[] = [];
    readonly pageSize = 10;
    /** Sent to M3 as maxrecs. 0 means all records. */
    maxRecords = 10;
    readonly maxRecordOptions = [
        { value: 10, label: 'Last 10' },
        { value: 20, label: 'Last 20' },
        { value: 0, label: 'All' }
    ];
    private requestId = 0;
    private suggestionRequestId = 0;

    constructor(private readonly itemDetailsService: ItemDetailsService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && this.visible) {
            const prefill = String(this.itemNo || '').trim();
            this.selectedItemNo = prefill;
            this.selectedSuggestion = prefill ? { MBITNO: prefill, FINALRESPNSE: prefill } : null;
            this.suggestions = [];
            this.searchingItems = false;
            this.maxRecords = 10;
            this.loading = false;
            this.searched = false;
            this.errorMessage = '';
            this.rows = [];
        }
    }

    get canSearch(): boolean {
        return !!String(this.customer || '').trim() && !!this.selectedItemNo && !this.loading;
    }

    get searchHint(): string {
        if (this.searchingItems) {
            return 'Searching items…';
        }
        if (!this.selectedItemNo) {
            return 'Type at least 3 characters, then select an item';
        }
        return '';
    }

    onVisibleChange(open: boolean): void {
        this.visible = open;
        this.visibleChange.emit(open);
    }

    searchItems(event: { query?: string }): void {
        const query = String(event?.query ?? '').trim();
        if (this.selectedItemNo && !this.queryMatchesSelectedItem(query)) {
            this.clearSelectedItem();
        }
        if (query.length < 3) {
            this.suggestions = [];
            this.searchingItems = false;
            return;
        }
        const requestId = ++this.suggestionRequestId;
        this.searchingItems = true;
        this.itemDetailsService.searchItemsV2(query + '*', this.company, this.division, this.warehouse).subscribe({
            next: (resp) => {
                if (requestId !== this.suggestionRequestId) {
                    return;
                }
                this.suggestions = this.formatSuggestions(resp);
                this.searchingItems = false;
            },
            error: () => {
                if (requestId !== this.suggestionRequestId) {
                    return;
                }
                this.suggestions = [];
                this.searchingItems = false;
                this.errorMessage = 'Unable to search items. Please try again.';
            }
        });
    }

    onItemSelected(event: any): void {
        const item = event?.value ?? event;
        const itemNo = String(item?.MBITNO ?? item?.itemNumber ?? '').trim();
        if (!itemNo) {
            this.errorMessage = 'Selected item has no item number.';
            this.selectedItemNo = '';
            return;
        }
        this.selectedSuggestion = item;
        this.selectedItemNo = itemNo;
        this.errorMessage = '';
        this.search();
    }

    onSearchModelChange(value: any): void {
        this.selectedSuggestion = value;
        const typed = this.currentQuery(value);
        if (this.selectedItemNo && (!typed || !this.queryMatchesSelectedItem(typed))) {
            this.clearSelectedItem();
        }
    }

    search(): void {
        const item = this.selectedItemNo.trim();
        const customer = String(this.customer || '').trim();
        const division = String(this.division || '').trim();
        if (!item || !customer || !division) {
            this.errorMessage = !customer
                ? 'Customer is required.'
                : !division
                  ? 'Division is required.'
                  : 'Select an item from the list.';
            this.rows = [];
            this.searched = false;
            return;
        }
        const requestId = ++this.requestId;
        this.loading = true;
        this.errorMessage = '';
        this.searched = false;
        this.itemDetailsService
            .getOldSalesPrices(this.company, division, this.warehouse, customer, item, this.maxRecords)
            .subscribe({
                next: (res) => {
                    if (requestId !== this.requestId) {
                        return;
                    }
                    this.loading = false;
                    this.searched = true;
                    this.rows = Array.isArray(res?.rows) ? res.rows : [];
                },
                error: (err) => {
                    if (requestId !== this.requestId) {
                        return;
                    }
                    this.loading = false;
                    this.searched = true;
                    this.rows = [];
                    this.errorMessage =
                        err?.error?.message || err?.message || 'Unable to load price history. Please try again.';
                }
            });
    }

    get recordLabel(): string {
        const count = this.rows.length;
        return count === 1 ? '1 price record' : `${count} price records`;
    }

    clearItem(): void {
        this.suggestionRequestId++;
        this.selectedSuggestion = null;
        this.suggestions = [];
        this.searchingItems = false;
        this.clearSelectedItem();
    }

    private clearSelectedItem(): void {
        this.requestId++;
        this.selectedItemNo = '';
        this.rows = [];
        this.searched = false;
        this.loading = false;
    }

    private currentQuery(value: any): string {
        if (value == null) {
            return '';
        }
        if (typeof value === 'string') {
            return value.trim();
        }
        return String(value?.FINALRESPNSE ?? value?.MBITNO ?? '').trim();
    }

    private queryMatchesSelectedItem(query: string): boolean {
        const selected = this.selectedItemNo.trim();
        if (!selected || !query) {
            return false;
        }
        return query === selected || query.startsWith(selected + ' | ') || query.startsWith(selected + ' ');
    }

    private formatSuggestions(resp: any[]): any[] {
        if (!Array.isArray(resp) || resp.length === 0) {
            return [];
        }
        return resp.map((item) => {
            const itno = String(item?.MBITNO ?? '').trim();
            const label = String(item?.FINALRESPNSE ?? '').trim();
            if (!itno || !label) {
                return item;
            }
            const separator = ' | ';
            if (label.startsWith(itno + separator) || label.startsWith(itno + ' ')) {
                return item;
            }
            return { ...item, FINALRESPNSE: `${itno}${separator}${label}` };
        });
    }

    onMaxRecordsChange(): void {
        if (this.selectedItemNo) {
            this.search();
        }
    }

    formatQty(value: string | undefined): string {
        const raw = String(value ?? '').trim();
        if (!raw) {
            return '';
        }
        const n = Number(raw);
        if (!Number.isFinite(n)) {
            return raw;
        }
        return String(n);
    }

    formatPrice(value: string | undefined): string {
        const raw = String(value ?? '').trim();
        if (!raw) {
            return '';
        }
        const n = Number(raw);
        if (!Number.isFinite(n)) {
            return raw;
        }
        return n.toFixed(2);
    }

    formatDate(value: string | undefined): string {
        const raw = String(value ?? '').trim();
        if (!/^\d{8}$/.test(raw)) {
            return raw;
        }
        const year = Number(raw.slice(0, 4));
        const month = Number(raw.slice(4, 6)) - 1;
        const day = Number(raw.slice(6, 8));
        const date = new Date(year, month, day);
        if (Number.isNaN(date.getTime())) {
            return raw;
        }
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
}
